import { IconBrandLinkedin, IconBrandYoutube } from '@tabler/icons-react';
import type { ComponentType } from 'react';
import type { HelpAction, SuggestionCategory } from 'agentic_chat/types';

export interface MockAIAgentData {
  helpActions: Record<string, HelpAction[]>;
}

export const AI_EDITS_PROGRESS_MESSAGES: readonly string[] = [
  'Analyzing your request...',
  'Processing video context...',
  'Preparing AI suggestions...',
  'Understanding your editing needs...',
  'Almost ready...',
  'Generating recommendations...'
] as const;

export const PROGRESS_MESSAGE_CONFIG = {
  INITIAL_DELAY_MS: 3000,
  INTERVAL_MS: 2000
} as const;

export interface SuggestionsData {
  [context: string]: SuggestionCategory[];
}

const AI_EDITS_HELP_ACTIONS: HelpAction[] = [
  {
    id: 'optimize-linkedIn',
    title: 'Optimize for LinkedIn',
    description: 'Create a professional and calm edit (Square)',
    prompt: '',
    context: 'AI_EDITS',
    icon: IconBrandLinkedin as ComponentType<{ size?: number; className?: string }>
  },
  {
    id: 'optimize-youtube',
    title: 'Optimize for YouTube',
    description: 'Create an engaging, dynamic short (Portrait)',
    prompt: '',
    context: 'AI_EDITS',
    icon: IconBrandYoutube as ComponentType<{ size?: number; className?: string }>
  }
];

// General Assistant Context Help Actions
const GENERAL_ASSISTANT_HELP_ACTIONS: HelpAction[] = [
  {
    id: 'content-suggestions',
    title: 'Content suggestions',
    description: 'Get ideas for improving your video content',
    prompt: '',
    context: 'GENERAL_ASSISTANT'
  },
  {
    id: 'editing-tips',
    title: 'Editing tips',
    description: 'Learn best practices for video editing',
    prompt: '',
    context: 'GENERAL_ASSISTANT'
  }
];

// Mock data structure organized by context
export const mockAIAgentData: MockAIAgentData = {
  helpActions: {
    AI_EDITS: AI_EDITS_HELP_ACTIONS,
    GENERAL_ASSISTANT: GENERAL_ASSISTANT_HELP_ACTIONS
  }
};

export const getHelpActionsByContext = (context: string): HelpAction[] => {
  return mockAIAgentData.helpActions[context] || [];
};

export const mockSuggestionsData: SuggestionsData = {
  AI_EDITS: [
    {
      title: 'Content',
      items: [
        {
          id: 'dynamic-layout-changes',
          name: 'Dynamic layout changes',
          tooltipContent:
            'Automatically apply scene changes to create a variation in layouts, switching between a focus on a single speaker and split screens.',
          prompt:
            'Add scene changes to add more variety – focus on the active speaker, and show an overview of all speakers when switching between active speakers.'
        }
      ]
    },
    {
      title: 'Enhancements',
      items: [
        {
          id: 'add-b-roll-videos',
          name: 'Add B-Roll videos',
          tooltipContent: 'Finds video footage for topics mentioned and adds them to the project as overlays.',
          prompt:
            'Add thematically relevant b-roll footage in moderation. Avoid adding b-roll at the beginning and end of the video.'
        },
        {
          id: 'add-background-music',
          name: 'Add background music',
          tooltipContent:
            'Enhances the project with soundtrack options, automatically balancing volume so dialogue remains clear.',
          prompt: 'Add background music that fits the mood of the video and adjust the volume to be pleasant.'
        },
        {
          id: 'add-quick-zooms',
          name: 'Add quick zooms',
          tooltipContent: 'Applies fast, attention-grabbing zoom effects to highlight speakers or key moments.',
          prompt: 'Add quick zooms whenever a question is asked, or a key concept or important phrase is mentioned.'
        },
        {
          id: 'add-jump-zooms',
          name: 'Add jump zooms',
          tooltipContent:
            'Creates quick cuts by shifting zoom levels, adding energy and pacing to keep viewers engaged.',
          prompt: 'Add jump zooms for subordinate clauses and fast sentences.'
        },
        {
          id: 'add-slow-zooms',
          name: 'Add slow zooms',
          tooltipContent: 'Introduces subtle, gradual zoom-ins to create a cinematic and engaging feel.',
          prompt: 'Add slow, constant zooms when a speaker is speaking for a long time.'
        },
        {
          id: 'create-text-callouts',
          name: 'Create text callouts',
          tooltipContent: 'Adds on-screen text highlights to emphasize key points, quotes, or topics.',
          prompt: 'Add callouts for important phrases, concepts and numbers.'
        }
      ]
    }
  ]
};

export const getSuggestionsByContext = (context: string) => mockSuggestionsData[context] || [];

export { AI_EDITS_HELP_ACTIONS, GENERAL_ASSISTANT_HELP_ACTIONS };
